import type { DismissReason } from "@/types/userTasks.ts";

/**
 * Why the user is dismissing a task, in their words rather than the enum's.
 *
 * Every reason detection has ever recorded, not just the ones still offered:
 * a task dismissed as "not important" before that row went still has to draw
 * its label.
 */
export const DISMISS_REASON_LABEL: Record<DismissReason, string> = {
  alreadyDone: "Already done",
  notImportant: "Not important",
  notATask: "Not a task",
  // Not a row to choose but a field to type in: "Other" because it sits under
  // the named reasons and means none of them. It is in this map all the same —
  // every reason has to be nameable, or a stored answer has nothing to show
  // under Closed.
  other: "Other reason",
};

/**
 * The row that opens the rule panel, where the user writes the rule in their
 * own words.
 *
 * "Create a rule" rather than naming the outcome, because the sheet these end
 * up in calls them "Rules you set": the verb that makes one and the noun it is
 * later found under are the same word.
 *
 * "for tasks like this", not "for this", because a rule is about a kind of
 * task and never about the one in hand — it applies to what detection reads
 * next and leaves this task exactly where it is. The group that held it used
 * to carry that in its heading; the heading is gone, so the button says it.
 *
 * Shared because the same menu is drawn twice, once per platform, and copy
 * that lives in both drifts. It already did: when muting was renamed, web's
 * half of this menu kept the old wording and its test went red unnoticed.
 */
export const CREATE_RULE_LABEL = "Create a rule for this";

/**
 * The empty rule field's hint: the sentence the user would say, rather than a
 * question about what to type. okthx drafts the rule from whatever they write,
 * so the hint's job is to set the voice — first person, about the kind and not
 * this one task — and to say plainly what saving one does, which is the part
 * "Create a rule" above it leaves out.
 */
export const RULE_WORDS_HINT = "Don't show me tasks like this.";

/**
 * The free-text dialog's own words, which are not the menu row's.
 *
 * The row says "Other reason" among the named ones, where the question is
 * already in the air. The dialog is its own surface and has to name the field
 * itself — a reader who opened it from the card's back, or came back to it
 * after the menu closed, has nothing else to say what it is for. It names the
 * field rather than instructing: the cursor is already in it.
 *
 * "Submit" rather than "Remove" on the button: the row above it already said
 * what this does, and a second "Remove" beside a half-typed sentence reads as
 * the destructive one rather than as sending what was written.
 */
export const GIVE_REASON_TITLE = "Reason for removal";
export const GIVE_REASON_SUBMIT = "Submit";

/**
 * The reasons a menu actually offers, which are fewer than the vocabulary.
 *
 * "Not important" and "Not a task" are left out: they answer the same
 * question for most people, and once the reader can write their own words a
 * fixed pair of near-synonyms only stands between them and saying the thing
 * they actually mean. Both stay in `dismissReasons` and `dropReasons` rather
 * than leaving the vocabulary — those enums back the server's own, and
 * narrowing them would make the API reject a reason it had already stored and
 * leave the tasks closed for it with no label to draw.
 *
 * `other` is last in both, and is drawn last in the menu: it is the way out
 * when none of the rows above fit, and a reader offered a blank field first
 * will take it every time.
 */
export const ASKED_DISMISS_REASONS = ["alreadyDone", "other"] as const;

/** A task already on the list cannot be "already done" — that is the Done button. */
export const ASKED_DROP_REASONS = ["other"] as const;

/**
 * What a card asks, which is only the reader's own words.
 *
 * The named reasons a row offers are answered on the card by the rows above
 * them — "already done" is the card's own Task is done — so what is left is
 * the one that is not a shade of anything: the reason none of them fit.
 */
export const ASKED_CARD_REASONS = ["other"] as const;

/**
 * The row's overflow, and the two things it holds that the card does not show
 * for itself.
 *
 * A note and a conversation are both worth doing and neither is what the row
 * is for: as outlined buttons they read as two more answers beside Done, and
 * on a card carrying three they wrapped the bar onto a second line.
 */
export const MORE_ACTIONS = "More actions";

export const ASK_ABOUT_TASK = "Ask about this";

/**
 * The heading over the overflow's last group, which says what those rows are.
 *
 * Without it they read as more actions: a mail subject in a list of verbs
 * looks like something the menu will do, not somewhere it will take you.
 * Counted, because a task often cites exactly one.
 */
export const sourcesHeading = (count: number) => (count === 1 ? "Source" : "Sources");

/**
 * Taking a task back off okthx, from the card stack's triage answers.
 *
 * Not the same answer as accepting a suggestion, and so it does not share
 * those words. A suggestion is offered and `ASSIGN_TO_ME` accepts it; this
 * interrupts work already under way, which "assign" understated by naming
 * only where the task lands.
 *
 * Opening on the same two words as `ASSIGN_TO_ME` on purpose: both are the
 * reader taking the task on, and the pair reads as one answer with a
 * difference rather than as two unrelated ones.
 */
export const TAKE_IT_BACK = "Take it back";

/**
 * Finishing a task, wherever it is answered.
 *
 * Its own answer rather than a shade of dismissal, because it is not one: the
 * task was real and it is over. Detection reads the difference — a dismissal
 * teaches it to stop offering tasks like this, where a task the user had
 * already done "counts as kept".
 *
 * One string for the button and the menu row alike. They were two — a verb on
 * the button, a sentence in the list — until the button took the sentence too,
 * and two names for one word is how the wording in one of them goes stale.
 *
 * "done" rather than "complete", to answer in the word the affirmative beside
 * it uses — {@link TASK_DONE_SHORT} — so finishing a task does not take two
 * verbs depending on which side of the pair reached it.
 */
export const TASK_IS_DONE = "Task is done";

/**
 * The same two answers where the row is narrow: a phone, and web below its
 * first breakpoint.
 *
 * Three buttons and an overflow do not fit a phone's width at full length,
 * and a row that wraps to two lines costs more than the words save. Desktop
 * has the room and says the whole thing.
 */
export const TASK_DONE_SHORT = "Done";

/**
 * Putting a task off the list, with no reason recorded.
 *
 * One answer where the dismissal reasons offered three. A row is read at leisure and each
 * reason teaches detection something different; a card is answered at speed,
 * and a reader made to pick between shades of no picks the nearest one, which
 * teaches detection worse than asking nothing would.
 *
 * "Drop", which is what the status route's own reasons are called, rather than
 * a softer word: the card has no snooze beside it any more, so this is the
 * only way off the list, and a label that read as "not now" would be attached
 * to the one answer there is no coming back from.
 *
 * It names the task rather than saying "it", to read as one of a pair with
 * {@link TASK_IS_DONE} beside it: both say what becomes of the task, in the
 * same breath.
 */
export const DROP_TASK = "Drop this task";

/**
 * Turning down a label okthx proposes. A proposal is not a task, so it does
 * not take {@link DISMISS_LABEL}: there is no menu of ways to put it down,
 * only this one answer, and what goes is the label.
 */
export const REMOVE_LABEL = "Remove";

/**
 * The control that opens a task's ways out, on every surface: a card's left
 * button, the compact row's leading swipe, and a full-width row's menu.
 *
 * The card and the compact row answer as a pair — this on one side, the
 * task's own affirmative on the other — so this side has to name the whole
 * half, which is every way of putting the task down and not only the one that
 * removes it. A full-width row stands a Done of its own beside this, which
 * leaves its menu only the ways off the list; it says the same word anyway,
 * because one control in one place cannot be two answers depending on what it
 * sits on.
 */
export const DISMISS_LABEL = "Dismiss";

/**
 * Accepting a suggestion from the card's triage answers: the reader takes it
 * on. The same two words {@link TAKE_IT_BACK} opens on, so the two read as one
 * answer with a difference rather than as two unrelated ones.
 */
export const ASSIGN_TO_ME = "I'll take it";
/**
 * The card's right-hand answer, which is half a card wide and carries a
 * chevron, so it says where the task goes and leaves the verb to the pair it
 * sits in — the other half already says what answering looks like.
 * `ASSIGN_TO_OKTHX` has a whole row and an "Assign" heading, and says it in
 * full.
 */
export const TO_OKTHX = "To Okthx";

/**
 * Taking it on, wherever that is the answer: a task of the reader's own that
 * wants a date, or one okthx is blocked on and only they can unblock.
 *
 * One phrase for both, because the reader is saying the same thing — this one
 * is mine — and the state decides what follows. Said in the past tense
 * because they are acknowledging something already theirs, where "I'll take
 * it" offered to accept something that had not been handed over.
 */
export const GOT_IT = "I got it";

/**
 * The reader is on it themselves, away from the app. Said as a state rather
 * than a promise ("I'm on it"), because it is also what the task says about
 * itself afterwards, in the Waiting group, until they mark it done.
 */
export const IN_PROGRESS = "In progress";

/**
 * The same answer as a row in the "I got it" menu: "now", because beside
 * "I'll do it by…" the bare state read as a label rather than a choice, and
 * the reader is saying what they are doing this minute.
 */
export const IN_PROGRESS_NOW = "In progress now";

/**
 * The heading over the calendar.
 *
 * In the reader's own voice, as the row that opens it is ("I'll do it by…"):
 * the two are one sentence, started in the menu and finished on the calendar,
 * so a heading phrased as an instruction read as the app telling them when to
 * be done rather than them saying it.
 *
 * "by", because a commitment is a deadline the reader set themselves rather
 * than a time they will be sitting down to it.
 */
export const COMMIT_TITLE = "I'll complete the task by";

/**
 * The step before a task is handed over, where the reader says what okthx
 * should know.
 *
 * It names the moment rather than instructing: the field below it is the
 * task's own note, already filled with whatever is there, and the reader is
 * being shown it rather than asked to start one.
 */
export const HAND_OFF_TITLE = "Before Okthx takes it";

/** The empty state of that field, which is the question it is really asking. */
export const HAND_OFF_HINT = "Anything Okthx should know?";

/**
 * The suggestion repeats a task already on the list.
 *
 * A noun phrase, because it sits among the other reasons for dismissing — "Not
 * important", "Not a task" — where an instruction like "Mark as duplicate"
 * would be the one row telling the user what to do rather than why. It names
 * the task it repeats: which one matters, and nothing else on the card says.
 */
export const duplicateOf = (title: string | null): string =>
  `Duplicate of "${title ?? "another task"}"`;

/**
 * Said after "Still waiting", which otherwise only takes the question away.
 *
 * It answers the question and resets the quiet timer, so okthx will ask again
 * if nothing arrives — but every part of that is invisible on the card. No
 * interval is promised: `silenceDays` is per account.
 *
 * Native speaks it rather than showing it: the card going quiet is answer
 * enough for someone reading it, and for someone who is not, nothing on
 * screen says the answer landed at all. Web still raises it as a toast, so
 * the name says what it is rather than where it goes.
 */
export const STILL_WAITING_CONFIRMATION = "Okay, I'll ask again if it stays quiet.";

/**
 * Said after a task goes to okthx, for the same reason: the row moving to
 * "Okthx on it" is the whole confirmation, and it is a silent one.
 */
export const HANDED_OFF_CONFIRMATION = "Okthx is on it";

/**
 * The check-in's other answer: the wait goes on.
 *
 * It answers the card's question — "Still waiting, or has it landed?" — rather
 * than naming an action, which is why it reads unlike Assign and Done beside
 * it. Not "Snooze": the snooze rows put the task itself down until a date,
 * where this leaves it open and waiting and only takes the question away.
 */
export const KEEP_WAITING = "Keep waiting";

/**
 * What a task okthx holds is doing, said the same way everywhere it is said.
 *
 * Three surfaces draw this line — each platform's compact row and the card's
 * own holder — and until now each held its own copy of the words. That is the
 * drift this file exists to stop: when muting was renamed, one half of a menu
 * kept the old wording and its test went red unnoticed.
 *
 * The line names okthx either way, because okthx is what is happening. Whose
 * move it is, is said beside it — by the group's own mark on a row, and on the
 * card by the face of whoever has to act next.
 */
export const OKTHX_NEEDS_YOU = "Okthx needs your input";
export const OKTHX_IS_WORKING = "Okthx is working on it";

/**
 * What a closed task says about itself, and the one move left.
 *
 * A closed task has exactly one answer — put it back — so the surface that
 * offers it is not a menu of ways out but a statement and a button. It names
 * the state rather than leaving "Re-open" to carry it alone: a reader who
 * swiped a card there has not necessarily read which group it came from.
 *
 * Closed only. A snoozed task can be reopened too, but it is parked rather
 * than answered and keeps its own ways out, so it never reaches this panel —
 * which is why there is no second line here to tell the two apart.
 */
export const TASK_IS_CLOSED = "This task is closed.";
export const REOPEN_TASK = "Re-open";

export const KEEP_TASK = "Keep";

/**
 * The rule field: what it is, and an example of what to write in it.
 *
 * Two strings rather than one. The label names the field; the hint sets the
 * voice okthx drafts from — first person, about the kind of task rather than
 * this one — and says plainly what saving a rule does to the task in hand,
 * which is the part "Create a rule" above it leaves out. Using the hint as the
 * label too would tell a screen reader what to type without ever saying what
 * the field is for.
 *
 * Both are drawn twice, once per platform, which is the drift this module
 * exists to stop.
 */
export const RULE_FIELD_LABEL = "What kind of task should Okthx leave out?";

/**
 * Putting a task down for a while, among the answers that put it down for
 * good.
 *
 * "Snooze until" rather than "Later": the row sits beside reasons for dropping
 * the task, where a bare "Tomorrow" would read as one more of them.
 *
 * One horizon. Next week asked the user how long to put a task down for, and
 * a task put down until tomorrow can be put down again tomorrow.
 */
export const SNOOZE_LABEL = "Snooze until tomorrow";

/** The thread okthx works a task in, named among its sources. It has no title of its own. */
export const OKTHX_CONVERSATION = "Okthx's conversation";

/**
 * The way from the answer dialog to the whole conversation. Beside the reply
 * field, not instead of it: one line answers most of what okthx asks, and
 * anything longer wants the thread.
 */
export const CONTINUE_IN_THREAD = "Continue in thread";
